import { environment } from '@environments/environment';
import { ChangeOrderService } from '@http/clients/reporting-app/change-order.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { formatCurrencyCompact, percentageFormatter, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import {
    changeOrderStatusEditorConfig,
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig,
    salePriceEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const ChangeOrders: IReportConfiguration<ChangeOrderService> = {
    base: {
        type: ReportType.ChangeOrders,
        serviceType: ChangeOrderService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getChangeOrdersBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getChangeOrdersMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'submittedOnHold', label: 'Submitted/On-hold' },
                        { name: 'rejectedCancel', label: 'Rejected/Canceled' },
                        { name: 'accepted', label: 'Accepted' }
                    ],
                    rows: [
                        { name: 'Qty', label: 'Quantity' },
                        { name: 'GrossMargin', label: 'Gross Margin (%)', format: 'customPercent' },
                        { name: 'SellPrice', label: 'Sell Price', format: 'currency' }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Change Orders by Sell Price'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'changeOrderName',
                            yKey: 'sellPrice',
                            yName: 'Sell Price'
                        },
                        {
                            type: 'line',
                            xKey: 'changeOrderName',
                            yKey: 'grossMargin',
                            yName: 'Gross Margin (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['sellPrice'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['grossMargin'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'sellPrice',
                            yName: 'Amount'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            keys: ['sellPrice'],
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.ExtendedTwoPlusOne
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getChangeOrdersValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'projectId',
                        headerName: 'BID',
                        enableRowGroup: true,
                        lockVisible: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (!params.value) {
                                return;
                            }
                            if (params.data?.projectArchived) {
                                return params.value;
                            } else {
                                const link = `${environment.SalesAppClientBaseUrl}/${environment.SalesAppClientProjectOverviewUrl}/${params.value}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                        }
                    },
                    { field: 'projectName', headerName: 'Bid Name', enableRowGroup: true },
                    {
                        field: 'oid',
                        headerName: 'OID',
                        lockVisible: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/opportunities-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return '';
                        }
                    },
                    { colId: 'projectNumber', field: 'projectNumber', headerName: 'Project Id' },
                    { field: 'projectStatus', headerName: 'Project Status', enableRowGroup: true },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'salesPerson', headerName: 'Salesperson', enableRowGroup: true },
                    { field: 'changeOrderId', headerName: 'Change Order ID' },
                    { field: 'changeOrderName', headerName: 'Change Order Name', enableRowGroup: true },
                    {
                        field: 'changeOrderDescription',
                        headerName: 'Change Order Description',
                        tooltipField: 'changeOrderDescription'
                    },
                    { field: 'changeOrderStatus', headerName: 'Change Order Status', enableRowGroup: true },
                    { field: 'changeOrderClientCONumber', headerName: 'Change Order Client CO#' },
                    { field: 'changeOrderReference', headerName: 'Change Order Reference' },
                    { field: 'tdc', headerName: 'Change Order TDC', type: 'currency', aggFunc: 'sum' },
                    { field: 'grossMargin', headerName: 'Gross Margin (%)', type: 'customPercentage', aggFunc: 'avg' },
                    { field: 'sellPrice', headerName: 'Sell Price', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'bidBaseContract',
                        headerName: 'Bid Base Contract (pre COs)',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'bidNewContract',
                        headerName: 'Bid New Contract (+ Accepted COs)',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'grossMarginPercent',
                        headerName: 'Bid Base GM% (pre COs)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'totalGrossMarginPercentage',
                        headerName: 'Bid New GM% (+ Accepted COs)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'projectDueDate', headerName: 'Project Due Date', type: 'date' },
                    { field: 'expectedBookingDate', headerName: 'Expected Booking Date', type: 'date' },
                    { field: 'projectStartDate', headerName: 'Project Start Date', type: 'date' },
                    { field: 'projectEndDate', headerName: 'Project End Date', type: 'date' },
                    { field: 'projectDuration', headerName: 'Project Duration' },
                    { field: 'markets', headerName: 'Market', type: 'array' },
                    { field: 'lead', headerName: 'Opportunity Source', enableRowGroup: true },
                    { field: 'division', headerName: 'Department', enableRowGroup: true },
                    { field: 'customer', headerName: 'Customer', enableRowGroup: true },
                    { field: 'account', headerName: 'Account', enableRowGroup: true },
                    {
                        field: 'opportunityLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'opportunityLastUpdatedNote'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            changeOrderStatusEditorConfig,
            salePriceEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig
        ]
    }
};
